import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache to avoid repeated API calls across components
let _cache = null;
let _cacheUserId = null;
let _cacheTime = 0;
const CACHE_TTL = 60_000; // 1 minute

// Export for clearing on logout
export const clearFeatureCache = () => {
  _cache = null;
  _cacheUserId = null;
  _cacheTime = 0;
};

export const useFeatureAccess = () => {
  const { user } = useAuth();
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    if (!user?.id) {
      setFeatures(null);
      setLoading(false);
      return;
    }

    // Admin/manager always have full access
    if (user.role === 'admin' || user.role === 'manager') {
      const full = {
        property_listing: true,
        ai_chat: true,
        maps_access: true,
        marketplace_listing: true,
        maps_browse_only: false,
        is_trial: false,
        trial_active: false,
        is_subscribed: true,
      };
      setFeatures(full);
      setLoading(false);
      return;
    }

    // Use cache if fresh
    const now = Date.now();
    if (_cache && _cacheUserId === user.id && (now - _cacheTime) < CACHE_TTL) {
      setFeatures(_cache);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/trial-management/user-features/${user.id}`);
      _cache = res.data;
      _cacheUserId = user.id;
      _cacheTime = Date.now();
      setFeatures(res.data);
    } catch {
      // On error, default to full access to avoid blocking users
      setFeatures({
        property_listing: true,
        ai_chat: true,
        maps_access: true,
        marketplace_listing: true,
        maps_browse_only: false,
        is_trial: false,
        is_subscribed: true,
      });
    }
    setLoading(false);
  }, [user?.id, user?.role]);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const canAccess = useCallback((feature) => {
    if (!features) return true; // Loading state: allow access
    return !!features[feature];
  }, [features]);

  const invalidateCache = useCallback(() => {
    _cache = null;
    _cacheTime = 0;
    fetchFeatures();
  }, [fetchFeatures]);

  return { features, loading, canAccess, invalidateCache };
};
